import { ExcludeBaseIcon } from '@tableau/icons/ExcludeBaseIcon';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import TagHttpClient from '~src/httpClient/TAG/TagHttpClient';

import { formatFileName, isCertPemFormat, isKeyPemFormat, readFileContent } from '../helpers/CustomDomainHelper';
import { UpdateCustomDomainTagRequest } from '../serverAPI/requests/AddOrUpdateCustomDomainTagRequest';
import { AddOrUpdateCustomDomainSettingRequest, CustomDomainSettingResponse } from '../serverAPI/types/CustomDomainTypes';
import { defaultTextAlignment,fileUploadContainerStyle,replaceSslParentContainerStyle,submitButtonStyle, uploadSslDefaultMarginStyle, uploadSslLabelStyle, uploadSslParentContainerStyle, uploadSslSubHeaderStyle, warningTextStyle } from '../styles/CustomDomainStyles';
import { FileUpload } from './FileUpload';
import { UploadedFileView } from './UploadedFileView';

export interface UploadSSLCertificateState {
  isUploading: boolean;
  certificate: File;
  privateKey: File;
  uploadedCertificateText: string;
  uploadedPrivateText: string;
}

export interface UploadSSLCertificateProps {
  customDomainSetting: CustomDomainSettingResponse;
  triggerFetchCustomDomain(): void;
  triggerCancelReplaceSsl?(): void;
}

let certificateTextStyle: React.CSSProperties = undefined;
let privateKeyTextStyle: React.CSSProperties = undefined;
export class UploadSSLCertificateView extends React.PureComponent<UploadSSLCertificateProps, UploadSSLCertificateState> {
    public constructor(props: UploadSSLCertificateProps) {
      super(props);
      this.state = {
        isUploading: false,
        certificate: undefined,
        privateKey: undefined,
        uploadedCertificateText: undefined,
        uploadedPrivateText: undefined
      };
    }

    //Asynchronous methods can't be rendered directly. So using 'state' to render the text
    public componentDidUpdate() {
      this.state.certificate ? this.validateCerificateAndRenderText(this.state.certificate) : null;
      this.state.privateKey ? this.validatePrivateKeyAndRenderText(this.state.privateKey) : null;
    }

    private isSubmitDisabled(): boolean {
      return this.state.certificate && this.state.privateKey && !this.hasWarnings()  ? false : true;
    }

    private hasWarnings(): boolean {
      return certificateTextStyle === warningTextStyle || privateKeyTextStyle === warningTextStyle;
    }

    private async validateCerificateAndRenderText(file: File){
      try {
        const isPemFile = await isCertPemFormat(file);
        if(!isPemFile) {
          certificateTextStyle =  warningTextStyle;
          this.setState({uploadedCertificateText: Messages.CustomDomainSetting_validPemFormat()});
        } else {
          certificateTextStyle =  defaultTextAlignment;
          this.setState({uploadedCertificateText: formatFileName(file)});
        }
      } catch (error) {
      certificateTextStyle =  warningTextStyle;
      this.setState({uploadedCertificateText: Messages.CustomDomainSetting_uploadFileFailed()});
      }
    }

    private async validatePrivateKeyAndRenderText(file: File){
      try {
        const isPemFile = await isKeyPemFormat(file);
        if(!isPemFile) {
          privateKeyTextStyle =  warningTextStyle;
          this.setState({uploadedPrivateText: Messages.CustomDomainSetting_validPemFormat()});
        } else {
          privateKeyTextStyle =  defaultTextAlignment;
          this.setState({uploadedPrivateText: formatFileName(file)});
        }
      } catch (error) {
      privateKeyTextStyle =  warningTextStyle;
      this.setState({uploadedPrivateText: Messages.CustomDomainSetting_uploadFileFailed()});
      }
    }

    private handleSubmit = async (): Promise<void> => {
      const certContent = await readFileContent(this.state.certificate);
      const keyContent = await readFileContent(this.state.privateKey);
      const customDomainSetting: AddOrUpdateCustomDomainSettingRequest = {
        site_luid: this.props.customDomainSetting.site_luid,
        custom_domain: this.props.customDomainSetting.custom_domain,
        status: this.props.customDomainSetting.status,
        //parsed file produces control character issues, which causes TAG to reject the call. Replacing \n manually with a string fixes the issue.
        tls_certificate: certContent.replace(/\n/g, '\n'),
        tls_certificate_private_key: keyContent.replace(/\n/g, '\n')
      };

      this.setState({isUploading: true});
        TagHttpClient.sendTagRequest(new UpdateCustomDomainTagRequest(customDomainSetting)).then((res: CustomDomainSettingResponse)  => {
          if(res.errors && res.errors.length > 0) {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.CustomDomainSetting_uploadSslCertFailedToast()});
          } else {
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.CustomDomainSetting_uploadSslCertSucceededToast()});
          }
         this.props.triggerFetchCustomDomain();
       }).finally(() => {
        this.setState({isUploading: false});
        this.props.triggerCancelReplaceSsl ? this.props.triggerCancelReplaceSsl() : undefined;
       });
    };

    private handleRemoveCertificate = (): void => {
      this.setState({uploadedCertificateText: undefined, certificate: undefined});
    };

    private handleRemovePrivateKey = (): void => {
      this.setState({uploadedPrivateText: undefined, privateKey: undefined});
    };

    private getViewMargin = () => {
      return this.props.triggerCancelReplaceSsl ? replaceSslParentContainerStyle : uploadSslParentContainerStyle;
    };

    public render() {
    return (
      <div style={this.getViewMargin()}>
        <div className={uploadSslLabelStyle}>
          {Messages.CustomDomainSetting_uploadSslCertLabel()}
          { this.state.uploadedCertificateText && certificateTextStyle !== warningTextStyle ?
              <UploadedFileView fileName={this.state.uploadedCertificateText} handleClose={() => this.handleRemoveCertificate()} tbTestId={'uploaded-cert-view'}/>
            :
            <>
              <div style={fileUploadContainerStyle}>
                <FileUpload promptText={Messages.CustomDomainSetting_uploadFileButton()} tbTestId='button-cert-file-upload'
                  onFileSelect={(selectedFile) => this.setState({ certificate: selectedFile })} />
                <div style={certificateTextStyle}>{ this.state.uploadedCertificateText ?
                  <> <ExcludeBaseIcon size={12} style={{marginRight: 5}}/>  {this.state.uploadedCertificateText} </>: null}
                </div>
              </div>
            </> }
        </div>

        <div className={uploadSslSubHeaderStyle}>
          <strong>{Messages.CustomDomainSetting_uploadPrivateKeyDescription()}</strong>
        </div>
        <div className={uploadSslLabelStyle}>
        {Messages.CustomDomainSetting_uploadPrivateKeyLabel()}
        {this.state.uploadedPrivateText && privateKeyTextStyle !== warningTextStyle ?
            <UploadedFileView  fileName={this.state.uploadedPrivateText} handleClose={() => this.handleRemovePrivateKey()}  tbTestId={'uploaded-private-key-view'}/>
          :
          <>
           <div style={fileUploadContainerStyle}>
              <FileUpload promptText={Messages.CustomDomainSetting_uploadFileButton()} tbTestId='button-key-file-upload'
              onFileSelect={(selectedFile) =>this.setState({privateKey: selectedFile})}/>
              <div style={privateKeyTextStyle}>{ this.state.uploadedPrivateText ?
                <> <ExcludeBaseIcon size={12} style={{marginRight: 5}}/>  {this.state.uploadedPrivateText} </> : null} </div>
            </div>
          </>}
        </div>
          <div style={uploadSslDefaultMarginStyle}>
            <div className={submitButtonStyle}>
              <ButtonWidget buttonType={ButtonType.Primary} showSpinner={this.state.isUploading} onClick={this.handleSubmit} testId='button-replace-ssl-submit' disabled={this.isSubmitDisabled()}>
                      {Messages.save()}
              </ButtonWidget>
            </div>
          </div>
      </div>
      );
    }
  }
